import {
  CLINIC_LOGO_MAX_HEIGHT,
  CLINIC_LOGO_MAX_WIDTH
} from "components/print-header/print-header-report/clinic-logo-constants";
import {
  CANADA_COUNTRY_VALUE,
  CANADA_PROVINCES,
  US_STATES,
  US_VIRGIN_ISLANDS_COUNTRY_VALUE,
  US_VIRGIN_ISLANDS_TERRITORIES
} from "constants/addresses";
import { type Clinic } from "services/types/clinics.type";
import { formatPhoneNumber } from "utils/common";
import { buildPracticeSlug, getPortalUrl } from "utils/portal-url.utils";

import { CLINIC_OPTIONS } from "../merge-tags-options/clinic.utils";
import {
  MergeTagsReplaceHandler,
  type MergeTagsReplaceHandlerParams
} from "./merge-tags-values";

// SN-19877: addresses as Anvil sends them, line 1 and 2 on one line and "City, ST ZIP", minus the
// stray spaces and commas Anvil leaves when a part is empty.
type AddressParts = {
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

const streetLine = ({ street1, street2 }: AddressParts) =>
  [street1, street2].filter(Boolean).join(" ");

const cityStateZipLine = ({ city, state, zip }: AddressParts) =>
  [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

const clinicAddress = (clinic: Clinic | undefined): AddressParts => ({
  street1: clinic?.street_address_1,
  street2: clinic?.street_address_2,
  city: clinic?.city,
  state: clinic?.state,
  zip: clinic?.zip_code
});

// The clinic's billing details (stored as `billng_*`, sic), each part falling back to the clinic's
// own, as Anvil's `billing_*` aliases do.
const billingAddress = (clinic: Clinic | undefined): AddressParts => {
  const billing = clinic?.extra;
  const own = clinicAddress(clinic);
  return {
    street1: billing?.billng_street_address_1 || own.street1,
    street2: billing?.billng_street_address_2 || own.street2,
    city: billing?.billng_city || own.city,
    state: billing?.billng_state || own.state,
    zip: billing?.billng_zip || own.zip
  };
};

const TOP_BAR_CLINIC_VALUES: MergeTagsReplaceHandler = {
  [CLINIC_OPTIONS.PRACTICE_NAME]: ({ data }) => {
    return data.currentClinic?.practice.name;
  },
  [CLINIC_OPTIONS.PRACTICE_LOGO]: ({ data }) => {
    const clinicLogo =
      data.currentClinic?.logo || data.currentClinic?.practice?.logo;
    const clinicName = data.currentClinic?.practice?.name;
    if (!clinicLogo) return null;

    return `<img src="${clinicLogo}" title="${clinicName} Logo" alt="${clinicName} Logo" style="max-height: ${CLINIC_LOGO_MAX_HEIGHT}; max-width:${CLINIC_LOGO_MAX_WIDTH}">`;
  },
  [CLINIC_OPTIONS.CLINIC_NAME]: ({ data }) => {
    return data.currentClinic?.name;
  },
  [CLINIC_OPTIONS.CLINIC_FULL_ADDRESS]: ({ data }) => {
    const clinic = data.currentClinic;

    return [
      clinic?.street_address_1,
      clinic?.street_address_2,
      clinic?.city,
      `${clinic?.state || ""} ${clinic?.zip_code || ""}`.trim()
    ]
      .filter(Boolean)
      .join(", ");
  },
  [CLINIC_OPTIONS.CLINIC_STREET_ADDRESS_LINE1]: ({ data }) => {
    return data.currentClinic?.street_address_1;
  },
  [CLINIC_OPTIONS.CLINIC_STREET_ADDRESS_LINE2]: ({ data }) => {
    return data.currentClinic?.street_address_2;
  },
  [CLINIC_OPTIONS.CLINIC_CITY]: ({ data }) => {
    return data.currentClinic?.city;
  },
  [CLINIC_OPTIONS.CLINIC_STATE]: ({ data }) => {
    const stateOptions =
      data.currentClinic?.country === CANADA_COUNTRY_VALUE
        ? CANADA_PROVINCES
        : data.currentClinic?.country === US_VIRGIN_ISLANDS_COUNTRY_VALUE
          ? US_VIRGIN_ISLANDS_TERRITORIES
          : US_STATES;
    return stateOptions.getFromCode(data.currentClinic?.state);
  },
  [CLINIC_OPTIONS.CLINIC_ZIP_CODE]: ({ data }) => {
    return data.currentClinic?.zip_code;
  },
  [CLINIC_OPTIONS.CLINIC_PHONE_NUMBER]: ({ data }) => {
    return formatPhoneNumber(data.currentClinic?.phone ?? "");
  },
  [CLINIC_OPTIONS.CLINIC_EMAIL]: ({ data }) => {
    return data.currentClinic?.email ?? "";
  },
  [CLINIC_OPTIONS.CLINIC_FAX_NUMBER]: ({ data }) => {
    return formatPhoneNumber(data.currentClinic?.fax_phone ?? "");
  },
  [CLINIC_OPTIONS.CLINIC_GOOGLE_MAP_URL]: ({ data, allowHtml }) => {
    if (!data.currentClinic?.google_place_id) return null;

    if (!allowHtml) {
      return `https://www.google.com/maps/place/?q=place_id:${data.currentClinic?.google_place_id}`;
    }

    return `<a title="${data.currentClinic.name} on Google Maps" href="https://www.google.com/maps/place/?q=place_id:${data.currentClinic?.google_place_id}" target="_blank">${data.currentClinic.name} on Google Maps</a>`;
  },
  [CLINIC_OPTIONS.CLINIC_BUSINESS_NAME]: ({ data }) => {
    return data.currentClinic?.display_name;
  },
  [CLINIC_OPTIONS.PRACTICE_PORTAL_LINK]: ({ data }) => {
    const practice = data.currentClinic?.practice;
    if (!practice?.name || !practice?.id) return null;

    const slug = buildPracticeSlug(practice.name);
    return `${getPortalUrl()}/${slug}/login?practice_id=${practice.id}`;
  },
  [CLINIC_OPTIONS.CLINIC_STREET_ADDRESS]: ({ data }) =>
    streetLine(clinicAddress(data.currentClinic)),
  [CLINIC_OPTIONS.CLINIC_CITY_STATE_ZIP]: ({ data }) =>
    cityStateZipLine(clinicAddress(data.currentClinic)),
  [CLINIC_OPTIONS.CLINIC_BILLING_STREET_ADDRESS]: ({ data }) =>
    streetLine(billingAddress(data.currentClinic)),
  [CLINIC_OPTIONS.CLINIC_BILLING_CITY_STATE_ZIP]: ({ data }) =>
    cityStateZipLine(billingAddress(data.currentClinic)),
  [CLINIC_OPTIONS.CLINIC_BILLING_FULL_ADDRESS]: ({ data }) => {
    const billing = billingAddress(data.currentClinic);
    return [billing.street1, billing.street2, cityStateZipLine(billing)]
      .filter(Boolean)
      .join(", ");
  },
  [CLINIC_OPTIONS.CLINIC_BILLING_PHONE_NUMBER]: ({ data }) =>
    formatPhoneNumber(
      data.currentClinic?.extra?.billng_phone || data.currentClinic?.phone
    ),
  [CLINIC_OPTIONS.PRACTICE_NPI]: ({ data }) => data.currentClinic?.practice?.npi
};

// ENG-2491: a Digital Form's surface can hand in the form's own clinic (`extraData.formClinic`: the sale's,
// the order's ship-to or the repair dialog's clinic). Every clinic and practice tag then reads that clinic
// instead of the one in the top bar; without it, each prints exactly what it printed before.
const clinicOf = ({ data, extraData }: MergeTagsReplaceHandlerParams) =>
  extraData?.formClinic ?? data.currentClinic;

export const CLINIC_VALUES: MergeTagsReplaceHandler = Object.fromEntries(
  Object.entries(TOP_BAR_CLINIC_VALUES).map(([tag, handler]) => [
    tag,
    (params: MergeTagsReplaceHandlerParams) =>
      handler({
        ...params,
        data: { ...params.data, currentClinic: clinicOf(params) }
      })
  ])
);
